/**
 * Admin Reports Service (spec 016 — 016-US-01/02/03/04/06/07)
 *
 * Calls the admin report endpoints on TaskManager.API. The ChellessAdmin
 * Vite proxy (vite.config.ts) routes /api → the TaskManager API, so relative
 * paths work in both dev and production.
 *
 * All functions use the established authenticatedFetch pattern from src/api/,
 * which injects the admin Bearer token automatically. The endpoints are
 * [Authorize(Roles = "Admin")] — a non-admin token yields 401/403.
 *
 * Interfaces are aligned 1-to-1 with the C# DTOs in TaskManager.Models per
 * specs/016-admin-reports/{contracts/admin-reports-api.md,data-model.md}.
 * ASP.NET serialises to camelCase by default — field names below match the
 * serialised camelCase form of the C# property names.
 *
 * Contract notes (FR-082): per-area failures inside composite responses are
 * reported as `{ area, error }` entries in a top-level `errors` array rather
 * than failing the whole payload — e.g. Survey.API being down yields a
 * populated getting-started stage plus `{ area: "survey", error: "…" }`.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';
import type { RetryConfig } from '../api/resilientFetch';

// ---------------------------------------------------------------------------
// Route/query enums (contract: admin-reports-api.md)
// ---------------------------------------------------------------------------

/** Funnel stage route segment (kebab-case, used in the URL path). */
export type FunnelStage = 'getting-started' | 'survey' | 'submission';

/** Stage user-state filter (query string value). */
export type FunnelStageState = 'started' | 'completed' | 'abandoned' | 'in-progress';

/**
 * AI generation run status (AITaskGeneration.ProcessingStatus, serialised as a
 * lowercase string; "fallback" = failed/rejected generation with a template
 * board delivered — see data-model.md state transitions).
 */
export type AiGenerationStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'fallback';

// ---------------------------------------------------------------------------
// Response shapes — aligned to TaskManager.Models DTOs (camelCase serialised)
// ---------------------------------------------------------------------------

/** Per-area failure entry inside a composite response (FR-082). */
export interface ReportAreaError {
  /** Report area that failed, e.g. "survey". */
  area: string;
  /** Human-readable failure description. */
  error: string;
}

/** Entry-path split for the getting-started stage. */
export interface FunnelStageEntryPath {
  /** Onboarding entry path, e.g. "browse", "skills", "idea", "budget". */
  entryPath: string;
  started: number;
  completed: number;
}

/**
 * One funnel stage row from OnboardingFunnelResponse.stages[].
 * data-model.md: `{ stage, started, completed, dropOffCount, dropOffPct, byEntryPath[] }`.
 */
export interface OnboardingFunnelStage {
  /** Stage id: getting-started | survey | submission. */
  stage: FunnelStage;
  /** Distinct users who started this stage in range (bucketed by start time). */
  started: number;
  /** Distinct users who completed this stage in range (bucketed by completion time). */
  completed: number;
  /** Users lost between the previous stage and this one. */
  dropOffCount: number;
  /** Drop-off percentage between the previous stage and this one (0-100). */
  dropOffPct: number;
  /** Entry-path split — populated for the getting-started stage, empty otherwise. */
  byEntryPath: FunnelStageEntryPath[];
  /** Survey stage only: sessions still in progress. null/absent for other stages. */
  inProgress?: number | null;
  /** Survey stage only: abandoned sessions. null/absent for other stages. */
  abandoned?: number | null;
}

/**
 * GET /api/admin/reports/onboarding-funnel response.
 * Maps to OnboardingFunnelResponse (TaskManager.Models).
 */
export interface OnboardingFunnelResponse {
  /** Echo of the requested range (ISO-8601 UTC). */
  from: string;
  to: string;
  /** Ordered stages: getting-started → survey → submission. */
  stages: OnboardingFunnelStage[];
  /** Per-area failures (FR-082). Empty when all areas resolved. */
  errors: ReportAreaError[];
}

/**
 * One user row in the funnel stage drill-down.
 * profileId is a GUID string (UserProfile.ProfileId is UNIQUEIDENTIFIER);
 * null for anonymous getting-started rows with no linked profile yet.
 */
export interface FunnelStageUserRow {
  profileId: string | null;
  displayName: string;
  email: string;
  /** ISO-8601 UTC timestamp when the user entered the stage. */
  startedAt: string;
  /** ISO-8601 UTC timestamp of last activity in the stage; null when unknown. */
  lastActivityAt: string | null;
  /** Progress within the stage, e.g. current step/section. null when unknown. */
  progressMarker: string | null;
}

/**
 * GET /api/admin/reports/onboarding-funnel/{stage}/users response.
 * Maps to FunnelStageUsersResponse (TaskManager.Models) — paginated.
 */
export interface FunnelStageUsersResponse {
  stage: FunnelStage;
  state: FunnelStageState;
  page: number;
  pageSize: number;
  totalCount: number;
  users: FunnelStageUserRow[];
}

// ---------------------------------------------------------------------------
// AI generation report (016-US-04)
// ---------------------------------------------------------------------------

/** Run counts per status in range. data-model.md: totals incl. fallback. */
export interface AiGenerationTotals {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  fallback: number;
}

/**
 * One daily bucket of successRateTrend[].
 * Contract leaves the bucket shape loose — assumed C#-DTO-style
 * `{ date, total, completed, successRatePct }` (successRatePct 0-100).
 */
export interface AiGenerationTrendBucket {
  /** Bucket day, ISO-8601 date (UTC). */
  date: string;
  /** Runs started in the bucket. */
  total: number;
  /** Runs completed successfully in the bucket. */
  completed: number;
  /** completed / total as a percentage (0-100); 0 when total is 0. */
  successRatePct: number;
}

/** One run row in AiGenerationReportResponse.runs[] (paginated). */
export interface AiGenerationRunRow {
  /** AITaskGeneration.GenerationId — GUID string (UNIQUEIDENTIFIER PK). */
  id: string;
  status: AiGenerationStatus;
  /** AI provider name, e.g. "openai", "claude". */
  provider: string;
  /** ISO-8601 UTC start (CreatedDate). */
  startedAt: string;
  /** ISO-8601 UTC completion (CompletedDate); null while pending/processing. */
  completedAt: string | null;
  /** Wall-clock duration in milliseconds; null while incomplete. */
  durationMs: number | null;
  tasksGenerated: number | null;
  /** Error text for failed/fallback runs; may be truncated in the list view. */
  errorMessage: string | null;
}

/**
 * GET /api/admin/reports/ai-generation response.
 * Pagination fields assumed at the top level (same style as
 * FunnelStageUsersResponse) — not spelled out in the contract.
 */
export interface AiGenerationReportResponse {
  from: string;
  to: string;
  totals: AiGenerationTotals;
  /** Daily success-rate buckets across the range. */
  successRateTrend: AiGenerationTrendBucket[];
  page: number;
  pageSize: number;
  /** Total run count for the current status filter (all pages). */
  totalCount: number;
  runs: AiGenerationRunRow[];
}

/**
 * GET /api/admin/reports/ai-generation/{id} response — triage view (FR-032).
 * Contract says "run detail incl. errorMessage, parameters summary"; the
 * extra AITaskGeneration fields (tokensUsed, costUsd, surveyResponseId,
 * generatedBoardId) are assumed from data-model.md's entity column list.
 */
export interface AiGenerationRunDetailResponse extends AiGenerationRunRow {
  /** Human-readable summary of the generation parameters/prompt inputs. */
  parametersSummary: string | null;
  tokensUsed: number | null;
  costUsd: number | null;
  surveyResponseId: number | null;
  /** Board produced by the run (or the fallback template board). GUID/id as string; null when none. */
  generatedBoardId: string | null;
}

// ---------------------------------------------------------------------------
// Boards report (016-US-03)
// ---------------------------------------------------------------------------

/** One daily bucket of BoardsReportResponse.trend[]. */
export interface BoardsTrendBucket {
  /** Bucket day, ISO-8601 date (UTC). */
  date: string;
  /** Boards created that day. */
  count: number;
}

/**
 * GET /api/admin/reports/boards response.
 * data-model.md: `{ totalBoards, createdInRange, byOrigin: { wizardGenerated, fallbackGenerated, other }, trend[] }`.
 */
export interface BoardsReportResponse {
  from: string;
  to: string;
  /** All non-deleted boards, regardless of range. */
  totalBoards: number;
  /** Boards created within the requested range. */
  createdInRange: number;
  /** Origin split for boards created in range. */
  byOrigin: {
    /** Created via the survey wizard with AITaskGeneration.ProcessingStatus == "Completed". */
    wizardGenerated: number;
    /** Created via the survey wizard but AITaskGeneration.ProcessingStatus == "Fallback". */
    fallbackGenerated: number;
    other: number;
  };
  trend: BoardsTrendBucket[];
}

// ---------------------------------------------------------------------------
// Engagement report (016-US-06)
// ---------------------------------------------------------------------------

/** Partner count per lifecycle state (PartnerLifecycleState, lowercase string). */
export interface PartnerLifecycleStateCount {
  /** e.g. "invited", "active", "lapsed", "expired". */
  state: string;
  count: number;
}

/** Investor invitation count per status (InvestorInviteStatus, lowercase string). */
export interface InvestorInvitationStatusCount {
  /** e.g. "sent", "opened", "accepted", "declined", "expired". */
  status: string;
  count: number;
}

/**
 * GET /api/admin/reports/engagement response.
 * data-model.md gives partner/investor halves; assumed nested as
 * `{ partner: {...}, investor: {...} }`. The senders drill-down is served by
 * the dedicated paginated endpoint, not embedded here.
 */
export interface EngagementReportResponse {
  from: string;
  to: string;
  partner: {
    /** Distinct board owners with at least one partner. */
    usersWithPartner: number;
    byLifecycleState: PartnerLifecycleStateCount[];
  };
  investor: {
    invitationsByStatus: InvestorInvitationStatusCount[];
  };
  /** Per-area failures (FR-082). Empty when all areas resolved. */
  errors: ReportAreaError[];
}

/**
 * One sender row in the investor-senders drill-down (FR-051).
 * Contract leaves row shape loose — assumed
 * `{ profileId, displayName, email, invitationsSent, acceptedCount, lastSentAt }`.
 */
export interface InvestorSenderRow {
  /** GUID string (UserProfile.ProfileId). */
  profileId: string;
  displayName: string;
  email: string;
  /** Invitations sent by this user in range. */
  invitationsSent: number;
  /** Of those, how many were accepted. */
  acceptedCount: number;
  /** ISO-8601 UTC timestamp of the most recent invitation; null when unknown. */
  lastSentAt: string | null;
}

/**
 * GET /api/admin/reports/engagement/investor-senders response — paginated,
 * same top-level pagination style as FunnelStageUsersResponse (assumed).
 */
export interface InvestorSendersResponse {
  page: number;
  pageSize: number;
  totalCount: number;
  senders: InvestorSenderRow[];
}

// ---------------------------------------------------------------------------
// Billing report (016-US-07)
// ---------------------------------------------------------------------------

/** Active-subscription count per tier. */
export interface TierDistributionEntry {
  /** Tier code/name, e.g. "free", "basic", "pro", "premium". */
  tier: string;
  count: number;
}

/**
 * One migration row in BillingReportResponse.migrationsInRange[].
 * Contract leaves row shape loose — assumed from the SubscriptionMigration
 * columns in data-model.md:
 * `{ profileId, displayName, fromTier, toTier, reason, migratedAt }`.
 */
export interface SubscriptionMigrationRow {
  /** GUID string (UserProfile.ProfileId). */
  profileId: string;
  displayName: string;
  fromTier: string;
  toTier: string;
  /** MigrationReason; null when not recorded. */
  reason: string | null;
  /** ISO-8601 UTC migration date. */
  migratedAt: string;
}

/**
 * GET /api/admin/reports/billing response. Reads local data only — never
 * calls Stripe (FR-061).
 */
export interface BillingReportResponse {
  from: string;
  to: string;
  tierDistribution: TierDistributionEntry[];
  migrationsInRange: SubscriptionMigrationRow[];
  /** AI credits purchased in range (AICreditsTransaction ledger); null/omitted when the read failed (the API omits nulls). */
  creditsPurchased?: number | null;
  /** AI credits consumed in range; null/omitted when the read failed. */
  creditsConsumed?: number | null;
  /** Per-area failures, area "creditsPurchased" / "creditsConsumed". Missing on older API builds. */
  errors?: ReportAreaError[];
}

// ---------------------------------------------------------------------------
// Feature usage report (016-US-08)
// ---------------------------------------------------------------------------

/** One ranked feature row in UsageReportResponse.topFeatures[]. */
export interface UsageTopFeature {
  /** Telemetry event name, e.g. "board.created". */
  eventName: string;
  /** Feature area grouping, e.g. "boards", "survey". */
  featureArea: string;
  /** Event occurrences in range. */
  count: number;
  /** Distinct users who emitted the event in range. */
  uniqueUsers: number;
}

/** One route row in UsageReportResponse.pageViews[]. */
export interface UsagePageView {
  /** Frontend route, e.g. "/boards". */
  route: string;
  count: number;
}

/** One daily bucket of UsageReportResponse.trend[]. */
export interface UsageTrendBucket {
  /** Bucket day, ISO-8601 date (UTC). */
  date: string;
  /** Events that day (respecting the featureArea filter). */
  count: number;
}

/**
 * GET /api/admin/reports/usage response.
 * `{ from, to, topFeatures[], pageViews[], trend[] }` per the 016-US-08 contract.
 */
export interface UsageReportResponse {
  from: string;
  to: string;
  topFeatures: UsageTopFeature[];
  pageViews: UsagePageView[];
  trend: UsageTrendBucket[];
}

// ---------------------------------------------------------------------------
// Admin user search + detail (016-US-05)
// ---------------------------------------------------------------------------

/** One user row in the admin user search results. */
export interface AdminUserSearchRow {
  /** GUID string (UserProfile.ProfileId). */
  profileId: string;
  displayName: string;
  email: string;
}

/** GET /api/admin/reports/users/search response — paginated. */
export interface AdminUserSearchResponse {
  page: number;
  pageSize: number;
  totalCount: number;
  users: AdminUserSearchRow[];
}

/** Profile header of the user 360 view. */
export interface AdminUserProfile {
  /** GUID string (UserProfile.ProfileId). */
  profileId: string;
  displayName: string;
  email: string;
}

/**
 * One onboarding journey row. Contract leaves row shape loose — assumed
 * `{ entryPath, status, startedAt, completedAt }` from the funnel data model.
 */
export interface UserOnboardingJourneyRow {
  /** Onboarding entry path, e.g. "browse", "skills", "idea", "budget". */
  entryPath: string;
  /** Journey status (lowercase string), e.g. "started", "completed", "abandoned". */
  status: string;
  startedAt: string;
  completedAt: string | null;
}

/**
 * One survey session row. Contract leaves row shape loose — assumed
 * `{ sessionId, status, startedAt, completedAt }`.
 */
export interface UserSurveySessionRow {
  /** GUID string session id. */
  sessionId: string;
  /** Session status (lowercase string), e.g. "in-progress", "completed", "abandoned". */
  status: string;
  startedAt: string;
  completedAt: string | null;
}

/** One board row in AdminUserDetailResponse.boards[]. */
export interface UserBoardRow {
  /** GUID string board id. */
  boardId: string;
  name: string;
  createdAt: string;
  /** Board origin, e.g. "wizard", "manual". */
  origin: string;
}

/**
 * One partner row. Contract gives `{ state, ... }` — the extra fields are
 * assumed `{ displayName, email, invitedAt }` (nullable).
 */
export interface UserPartnerRow {
  /** Partner lifecycle state (lowercase string), e.g. "invited", "active". */
  state: string;
  displayName: string | null;
  email: string | null;
  invitedAt: string | null;
}

/** One investor invitation row: `{ status, sentAt, openedAt|null }`. */
export interface UserInvestorInvitationRow {
  /** Invitation status (lowercase string), e.g. "sent", "opened", "accepted". */
  status: string;
  sentAt: string;
  openedAt: string | null;
  /** Assumed extra field; null when not exposed. */
  inviteeEmail?: string | null;
}

/** One tier-history row — assumed same shape as SubscriptionMigrationRow sans profile. */
export interface UserTierHistoryRow {
  fromTier: string;
  toTier: string;
  reason: string | null;
  migratedAt: string;
}

/** AI credits summary tiles. null/omitted totals (the API omits nulls via IgnoreNullValues) mean "not measured" — show "—", never 0. */
export interface UserCreditsSummary {
  balance: number;
  totalPurchased?: number | null;
  totalUsed?: number | null;
}

/**
 * One credit/billing transaction row. Contract leaves row shape loose —
 * assumed `{ transactionId, type, amount, createdAt, description }`.
 */
export interface UserCreditTransactionRow {
  transactionId: string;
  /** Transaction type (lowercase string), e.g. "purchase", "consumption". */
  type: string;
  amount: number;
  createdAt: string;
  description: string | null;
}

/** Billing half of the user 360 view. */
export interface UserBillingSummary {
  /** Current tier code, e.g. "free", "pro". */
  tier: string;
  tierHistory: UserTierHistoryRow[];
  /** null/omitted when the Profile.API credit read failed; top-level errors then gets { area: "credits" }. */
  credits?: UserCreditsSummary | null;
  transactions: UserCreditTransactionRow[];
}

/**
 * GET /api/admin/reports/users/{profileId} response — whole-account 360 view
 * (FR-040). Empty areas come back as empty arrays; per-area failures land in
 * `errors` (FR-082). `billing` is assumed nullable when the billing area errors.
 */
export interface AdminUserDetailResponse {
  profile: AdminUserProfile;
  onboarding: UserOnboardingJourneyRow[];
  surveySessions: UserSurveySessionRow[];
  boards: UserBoardRow[];
  partners: UserPartnerRow[];
  investorInvitations: UserInvestorInvitationRow[];
  billing: UserBillingSummary | null;
  errors: ReportAreaError[];
}

// ---------------------------------------------------------------------------
// Overview (owner dashboard, US-17-GR-27)
// ---------------------------------------------------------------------------

/** One daily bucket of OverviewNewSignups.dailySeries[] — the dashboard sparkline series. */
export interface OverviewSignupsDailyPoint {
  /** Bucket day, ISO-8601 date (UTC). */
  date: string;
  count: number;
}

/** One tier row of OverviewTierDistribution.tiers[]. Not limited to Basic/Pro/Premium — the
 * legacy tier enum includes Entrepreneur/Growth/Elite and others; render whatever comes back. */
export interface OverviewTierRow {
  tier: string;
  count: number;
}

/**
 * OverviewReportResponse.tierDistribution — active-subscriber counts per tier plus the
 * server-computed estimated MRR (only basic+premium are priced today; display the value
 * labeled "estimated", never recompute it client-side). No prior-period value — render
 * without a vs-prior delta.
 */
export interface OverviewTierDistribution {
  tiers: OverviewTierRow[];
  estimatedMrrUsd: number;
}

/**
 * OverviewReportResponse.newSignups — current vs. prior-period signup counts (equal-length
 * period ending at OverviewReportResponse.priorTo) plus the daily series for the sparkline.
 */
export interface OverviewNewSignups {
  current: number;
  prior: number;
  /** Daily signup counts across [from, to], oldest first. */
  dailySeries: OverviewSignupsDailyPoint[];
}

/** One period's onboarding funnel stats — used for both OverviewFunnel.current and .prior. */
export interface OverviewFunnelPeriod {
  starts: number;
  submissions: number;
  /** submissionConverted / submissionStarted as a percentage, clamped 0-100. */
  conversionPct: number;
  /** Conversion denominator. Absent on an API that predates story 17788. */
  submissionStarted?: number;
  /** Conversion numerator, always <= submissionStarted. Absent on an older API. */
  submissionConverted?: number;
  /** false = no submissions started in the window (no data, NOT 0%). Absent on an older API. */
  conversionHasData?: boolean;
}

/** OverviewReportResponse.funnel — current vs. prior-period onboarding funnel. */
export interface OverviewFunnel {
  current: OverviewFunnelPeriod;
  prior: OverviewFunnelPeriod;
}

/** OverviewReportResponse.users — total/active user counts. No prior-period value. */
export interface OverviewUsers {
  totalUsers: number;
  activeUsersLast30Days: number;
}

/**
 * `getOverviewReport`, `getOnboardingFunnel`, `getFunnelStageUsers`, `getEngagementReport`,
 * `getInvestorSenders` and `getUsageReport` below all read data that is sourced from
 * `UsageEvent` rows, which only exist once the frontend has posted them via
 * `POST /api/usage-events/batch` and the API has served them back through
 * `AdminReportsController`.
 *
 * Until 2026-09-20, the PROD API predated both endpoints, so these reports came back
 * absent, not empty, on PROD. That is no longer current; see the 2026-09-27 correction in
 * docs/prod-api-skew-2026-09-10.md and work item 17576.
 */

/**
 * GET /api/admin/reports/overview response (US-17-GR-27 owner dashboard).
 * Every top-level section is independently nullable — a section being null
 * means that area's data isn't available for this range (see `errors` for
 * why); the dashboard degrades only the tile(s) fed by that section.
 * `priorFrom`/`priorTo` mark the equal-length period immediately preceding
 * [from, to], used for the newSignups/funnel vs-prior deltas. tierDistribution
 * and users carry no prior value — those tiles render without a delta.
 */
export interface OverviewReportResponse {
  from: string;
  to: string;
  priorFrom: string;
  priorTo: string;
  tierDistribution: OverviewTierDistribution | null;
  newSignups: OverviewNewSignups | null;
  funnel: OverviewFunnel | null;
  users: OverviewUsers | null;
  /** Per-area failures (FR-082 style). Empty when all sections resolved. */
  errors: ReportAreaError[];
}

/**
 * GET /api/admin/reports/overview?from&to
 * Owner dashboard summary (US-17-GR-27): estimated MRR + tier distribution,
 * new signups + onboarding conversion, and total/active user counts for the
 * requested range plus the equal-length prior period for vs-prior deltas.
 */
export async function getOverviewReport(
  from: string,
  to: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<OverviewReportResponse> {
  const qs = new URLSearchParams({ from, to });
  return get<OverviewReportResponse>(`/api/admin/reports/overview?${qs.toString()}`, retryConfig);
}

// ---------------------------------------------------------------------------
// Activation funnel (spec 017-retention-activation, US-14-GR-13) —
// signup → activated → realized. Served by Profile.API, NOT TaskManager.API —
// a different base route from every other report on this page.
// ---------------------------------------------------------------------------

/**
 * Canonical Stage-1 event names, in funnel order (Profile.Models.Dtos.ActivationEventNames,
 * spec 010-activation-pack). `stages[]` always carries exactly these 5 rows — the spec 017
 * "realized" stage is a separate nested field below, never a 6th row (confirmed against the
 * API implementation: an existing integration test asserts `Stages.Should().HaveCount(5)`).
 */
export type ActivationFunnelEventName =
  | 'signup_completed'
  | 'wizard_completed'
  | 'board_created'
  | 'first_run_queued'
  | 'first_deliverable_viewed';

/** One Stage-1 ("activated") funnel row. */
export interface ActivationFunnelStage {
  eventName: ActivationFunnelEventName;
  /** Distinct cohort profiles that reached this stage. */
  count: number;
  /** Stage-over-stage conversion 0-1 (1.0 for the anchor/signup stage when count > 0). */
  conversionFromPrevious: number;
}

/**
 * Stage 2 — "realized" (first dollar of revenue; DEC-2026-07-31 amends spec 010's
 * one-stage north star to this two-stage model). Self-reported by design (honor system,
 * Mark-as-Sold confirmation, DEC-001) — rendering this stage must never imply independent
 * verification.
 *
 * `medianDaysActivatedToRealized` is null until at least one cohort profile has reached
 * BOTH `first_deliverable_viewed` and `first_sale_confirmed` in range — render that
 * explicitly as "no data yet". Never render null as 0: a 0 here reads as "users convert
 * instantly", which is the opposite of the truth.
 */
export interface ActivationFunnelRealizedStage {
  eventName: 'first_sale_confirmed';
  count: number;
  /** Conversion from first_deliverable_viewed (the last Stage-1 row), 0-1. */
  conversionFromPrevious: number;
  medianDaysActivatedToRealized: number | null;
}

/**
 * GET /api/profile/activation/funnel response (spec 010-activation-pack T042, extended by
 * spec 017 US-14-GR-13 / FR-004). `realized` is absent/undefined when the responding API
 * build predates the spec 017 rollout — render that as an "unavailable" degraded state for
 * just that stage, distinct from a present `realized` whose median is null ("no data yet").
 * The rest of the funnel (stages[], activationRate) always renders regardless.
 */
export interface ActivationFunnelResponse {
  from: string;
  to: string;
  /** Ordered: signup → wizard → board → first run → deliverable. Always 5 rows. */
  stages: ActivationFunnelStage[];
  /** North star (spec 010): first_deliverable_viewed ÷ signup_completed, 0-1. */
  activationRate: number;
  /** Stage 2 (spec 017) — undefined on API builds that predate this rollout. */
  realized?: ActivationFunnelRealizedStage;
}

/**
 * GET /api/profile/activation/funnel?from&to
 * Activation funnel (US-14-GR-13): signup → activated → realized, with stage-over-stage
 * conversion and (once the realized stage has data) median days from activated to realized.
 * Admin-only.
 *
 * This route lives on Profile.API, not TaskManager.API — the host `get()` below dials by
 * default (work item 17417). Mirrors the PROFILE_BASE pattern in adminProfileClient.ts:
 * an absolute URL bypasses authenticatedFetch's API_BASE (TaskManager) prefixing, so this
 * goes straight to VITE_PROFILE_API_URL; falls back to the relative path (Vite proxy) when
 * that var is unset, same as adminProfileClient.ts.
 */
const PROFILE_BASE: string = (import.meta.env.VITE_PROFILE_API_URL as string | undefined) ?? '';

export async function getActivationFunnel(
  from: string,
  to: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<ActivationFunnelResponse> {
  const qs = new URLSearchParams({ from, to });
  return get<ActivationFunnelResponse>(`${PROFILE_BASE}/api/profile/activation/funnel?${qs.toString()}`, retryConfig);
}

// ---------------------------------------------------------------------------
// Deliverables-produced interim number (work item 19001, Stage 2b, DEC-2026-09-19)
// ---------------------------------------------------------------------------

/**
 * GET /api/admin/orchestrator/deliverables-produced response (contract C2).
 *
 * DEC-2026-09-19: a delivery-pipeline health number, NOT an activation count — the
 * API deliberately returns `label`/`subLabel`/`sunsetOn` alongside `value` so the
 * three can never ship apart or drift out of sync with a client-side hard-coded
 * string. Render exactly what the API returns; do not recompute or restate these
 * strings anywhere else. Sunsets 2026-12-18 per the decision.
 */
export interface DeliverablesProducedResponse {
  value: number;
  /** Expected to be exactly "Deliverables produced" — assert this in tests. */
  label: string;
  /** Expected to be exactly "delivery-pipeline health -- not an activation count". */
  subLabel: string;
  /** ISO-8601 date this interim metric sunsets. Expected "2026-12-18". */
  sunsetOn: string;
}

/**
 * GET /api/admin/orchestrator/deliverables-produced?from&to
 * Stage 2b interim number (work item 19001): count of deliverables the pipeline
 * produced in range. Explicitly NOT an activation measure and NEVER a CAC input —
 * see the sub-label the API returns and DEC-2026-09-19.
 */
export async function getDeliverablesProduced(
  from: string,
  to: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<DeliverablesProducedResponse> {
  const qs = new URLSearchParams({ from, to });
  return get<DeliverablesProducedResponse>(`/api/admin/orchestrator/deliverables-produced?${qs.toString()}`, retryConfig);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Thrown for non-OK admin report responses; carries the HTTP status (e.g. 404). */
export class AdminReportsHttpError extends Error {
  readonly status: number;

  constructor(status: number, path: string) {
    super(`Admin reports request failed: HTTP ${status} ${path}`);
    this.name = 'AdminReportsHttpError';
    this.status = status;
  }
}

async function get<T>(path: string, retryConfig?: Partial<RetryConfig>): Promise<T> {
  const response = await authenticatedFetch(path, undefined, retryConfig ? { retryConfig } : undefined);
  if (!response.ok) {
    throw new AdminReportsHttpError(response.status, path);
  }
  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * GET /api/admin/reports/onboarding-funnel?from&to
 * Onboarding funnel: getting-started → survey → submission, with entry-path
 * split, survey segmentation, and per-area `{ area, error }` entries.
 *
 * @param from ISO-8601 UTC range start (required by the API)
 * @param to   ISO-8601 UTC range end (required by the API)
 */
export async function getOnboardingFunnel(
  from: string,
  to: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<OnboardingFunnelResponse> {
  const qs = new URLSearchParams({ from, to });
  return get<OnboardingFunnelResponse>(`/api/admin/reports/onboarding-funnel?${qs.toString()}`, retryConfig);
}

/**
 * GET /api/admin/reports/onboarding-funnel/{stage}/users?state&from&to&page&pageSize
 * Paginated drill-down of users in a funnel stage/state (016-US-02).
 */
export async function getFunnelStageUsers(
  stage: FunnelStage,
  state: FunnelStageState,
  from: string,
  to: string,
  page: number = 1,
  pageSize: number = 25,
  retryConfig?: Partial<RetryConfig>,
): Promise<FunnelStageUsersResponse> {
  const qs = new URLSearchParams({
    state,
    from,
    to,
    page: String(page),
    pageSize: String(pageSize),
  });
  return get<FunnelStageUsersResponse>(
    `/api/admin/reports/onboarding-funnel/${encodeURIComponent(stage)}/users?${qs.toString()}`,
    retryConfig,
  );
}

/**
 * GET /api/admin/reports/ai-generation?from&to&status&page&pageSize
 * AI generation pipeline report (016-US-04): per-status totals, daily
 * success-rate trend, and the paginated (optionally status-filtered) run list.
 */
export async function getAiGenerationReport(
  from: string,
  to: string,
  status?: AiGenerationStatus,
  page: number = 1,
  pageSize: number = 25,
  retryConfig?: Partial<RetryConfig>,
): Promise<AiGenerationReportResponse> {
  const qs = new URLSearchParams({ from, to, page: String(page), pageSize: String(pageSize) });
  if (status) qs.set('status', status);
  return get<AiGenerationReportResponse>(`/api/admin/reports/ai-generation?${qs.toString()}`, retryConfig);
}

/**
 * GET /api/admin/reports/ai-generation/{id}
 * Single-run triage detail (FR-032): full error message + parameters summary.
 */
export async function getAiGenerationRun(
  id: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<AiGenerationRunDetailResponse> {
  return get<AiGenerationRunDetailResponse>(
    `/api/admin/reports/ai-generation/${encodeURIComponent(id)}`,
    retryConfig,
  );
}

/**
 * GET /api/admin/reports/boards?from&to
 * Boards report (016-US-03): totals, wizard-vs-other origin split, daily trend.
 */
export async function getBoardsReport(
  from: string,
  to: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<BoardsReportResponse> {
  const qs = new URLSearchParams({ from, to });
  return get<BoardsReportResponse>(`/api/admin/reports/boards?${qs.toString()}`, retryConfig);
}

/**
 * GET /api/admin/reports/engagement?from&to
 * Engagement report (016-US-06): partner + investor aggregates.
 */
export async function getEngagementReport(
  from: string,
  to: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<EngagementReportResponse> {
  const qs = new URLSearchParams({ from, to });
  return get<EngagementReportResponse>(`/api/admin/reports/engagement?${qs.toString()}`, retryConfig);
}

/**
 * GET /api/admin/reports/engagement/investor-senders?from&to&page&pageSize
 * Paginated investor-invitation senders drill-down (FR-051).
 */
export async function getInvestorSenders(
  from: string,
  to: string,
  page: number = 1,
  pageSize: number = 25,
  retryConfig?: Partial<RetryConfig>,
): Promise<InvestorSendersResponse> {
  const qs = new URLSearchParams({ from, to, page: String(page), pageSize: String(pageSize) });
  return get<InvestorSendersResponse>(
    `/api/admin/reports/engagement/investor-senders?${qs.toString()}`,
    retryConfig,
  );
}

/**
 * GET /api/admin/reports/billing?from&to
 * Billing report (016-US-07): tier distribution, migrations in range, and the
 * credits purchased/consumed totals. Local data only — never calls Stripe.
 */
export async function getBillingReport(
  from: string,
  to: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<BillingReportResponse> {
  const qs = new URLSearchParams({ from, to });
  return get<BillingReportResponse>(`/api/admin/reports/billing?${qs.toString()}`, retryConfig);
}

/**
 * GET /api/admin/reports/usage?from&to&featureArea
 * Feature usage report (016-US-08): ranked top features, page views, and the
 * daily event trend. `featureArea` optionally scopes all three sections.
 */
export async function getUsageReport(
  from: string,
  to: string,
  featureArea?: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<UsageReportResponse> {
  const qs = new URLSearchParams({ from, to });
  if (featureArea) qs.set('featureArea', featureArea);
  return get<UsageReportResponse>(`/api/admin/reports/usage?${qs.toString()}`, retryConfig);
}

/**
 * GET /api/admin/reports/users/search?q&page&pageSize
 * Paginated admin user search (016-US-05) — entry point to the user 360 view.
 */
export async function searchAdminUsers(
  q: string,
  page: number = 1,
  pageSize: number = 25,
  retryConfig?: Partial<RetryConfig>,
): Promise<AdminUserSearchResponse> {
  const qs = new URLSearchParams({ q, page: String(page), pageSize: String(pageSize) });
  return get<AdminUserSearchResponse>(`/api/admin/reports/users/search?${qs.toString()}`, retryConfig);
}

/**
 * GET /api/admin/reports/users/{profileId}
 * Whole-account user 360 view (016-US-05, FR-040). Throws
 * AdminReportsHttpError with status 404 for an unknown profileId.
 */
export async function getAdminUserDetail(
  profileId: string,
  retryConfig?: Partial<RetryConfig>,
): Promise<AdminUserDetailResponse> {
  return get<AdminUserDetailResponse>(
    `/api/admin/reports/users/${encodeURIComponent(profileId)}`,
    retryConfig,
  );
}

export const adminReportsService = {
  getOnboardingFunnel,
  getFunnelStageUsers,
  getAiGenerationReport,
  getAiGenerationRun,
  getBoardsReport,
  getEngagementReport,
  getInvestorSenders,
  getBillingReport,
  getUsageReport,
  searchAdminUsers,
  getAdminUserDetail,
  getOverviewReport,
  getActivationFunnel,
  getDeliverablesProduced,
};
